import {
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    inject,
    InjectionToken,
    Output,
    Renderer2
} from '@angular/core';
import {BehaviorSubject, delay, Observable, of, pairwise, Subject} from 'rxjs';
import {DialogHiddenState, DialogType} from '~shared/types/dialog.types';
import {startWith, switchMap} from 'rxjs/operators';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {DialogService} from '~shared/ng/services/dialog/dialog.service';

export const DIALOG_COMPONENT_OPTIONS = new InjectionToken<any>('DIALOG_COMPONENT_OPTIONS');

@Component({
    template: '',
})
export abstract class DialogBaseComponent<O = {}, R = any> {
    public static CLASS_SHOWING = 'showing';
    public static CLASS_HIDING = 'hiding';

    public readonly options?: O = inject(DIALOG_COMPONENT_OPTIONS);
    private readonly elementRef = inject(ElementRef);
    private readonly renderer = inject(Renderer2);

    @HostBinding('attr.tabindex')
    public readonly hostAttrTabIndex = '-1';
    @HostBinding('attr.role')
    public readonly hostAttrRole = 'dialog';
    @HostBinding('style.display')
    public readonly hostStyleDisplay = 'none';

    @Output('onClose')
    public readonly onClose$ = new EventEmitter<R | false>();

    public hidden$: Subject<void> = new Subject<void>();
    private readonly shouldShow$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    public abstract type: DialogType;

    constructor() {
        this.shouldShow$.pipe(
            switchMap(($: boolean): Observable<DialogHiddenState> => {
                return of<DialogHiddenState>({
                    transition: false,
                    hidden: $
                }).pipe(
                    delay(300),
                    startWith<DialogHiddenState>({
                        transition: true,
                        hidden: $
                    }),
                );
            }),
            startWith<DialogHiddenState>({
                transition: false,
                hidden: true
            }),
            pairwise(),
            takeUntilDestroyed()
        )
            .subscribe(([$old, $new]) => {
                if ($old.hidden !== $new.hidden) {
                    // Hidden state has changed, required actions should be performed.
                    if ($new.hidden) {
                        if (this.type === DialogType.OFFCANVAS) {
                            this.renderer.addClass(this.elementRef.nativeElement, DialogBaseComponent.CLASS_HIDING);
                        } else {
                            this.renderer.removeClass(this.elementRef.nativeElement, DialogService.CLASS_SHOW);
                        }
                        if ($new.transition === false) {
                            this.renderer.setStyle(this.elementRef.nativeElement, 'display', 'none');
                        }
                    } else {
                        setTimeout(() => {
                            if (this.type === DialogType.OFFCANVAS) {
                                this.renderer.removeStyle(this.elementRef.nativeElement, 'display');
                                setTimeout(() => {
                                    this.renderer.addClass(this.elementRef.nativeElement, DialogBaseComponent.CLASS_SHOWING);
                                }, 50);
                            } else {
                                this.renderer.setStyle(this.elementRef.nativeElement, 'display', 'block');
                                setTimeout(() => {
                                    this.renderer.addClass(this.elementRef.nativeElement, DialogService.CLASS_SHOW);
                                }, 50);
                            }
                        });
                    }
                } else if ($old.transition !== $new.transition) {
                    // Transition status has changed, required actions should be applied.
                    if ($new.hidden) {
                        // Finished hiding.
                        this.renderer.setStyle(this.elementRef.nativeElement, 'display', 'none');

                        if (this.type === DialogType.OFFCANVAS) {
                            this.renderer.removeClass(this.elementRef.nativeElement, DialogBaseComponent.CLASS_HIDING);
                        }
                        // trigger for close component
                        this.hidden$.next();
                    } else {
                        // Finished showing.
                        if (this.type === DialogType.OFFCANVAS) {
                            this.renderer.addClass(this.elementRef.nativeElement, DialogService.CLASS_SHOW);
                            this.renderer.removeClass(this.elementRef.nativeElement, DialogBaseComponent.CLASS_SHOWING);
                        }
                    }
                }
            });
    }

    public close($value: R | false = false): void {
        this.onClose$.emit($value);
        this.shouldShow$.next(true);
    }
}
